/**
 * Notifications Step
 * 
 * Request browser notification permission.
 * Issue #563 - First-Run Onboarding Flow
 */

import { useState } from 'react';

interface NotificationsStepProps {
  onNext: () => void;
  onBack: () => void;
}

export default function NotificationsStep({ onNext, onBack }: NotificationsStepProps) {
  const [status, setStatus] = useState<'idle' | 'requesting' | 'granted' | 'denied'>('idle');

  const handleEnableNotifications = async () => {
    setStatus('requesting');
    
    try {
      if (!('Notification' in window)) {
        // Browser doesn't support notifications
        setStatus('idle');
        onNext();
        return;
      }

      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        setStatus('granted');
        setTimeout(() => onNext(), 500);
      } else {
        setStatus('denied');
      }
    } catch (error) {
      console.error('Notification permission error:', error);
      setStatus('idle');
      onNext(); // Continue anyway
    }
  };

  const handleSkip = () => {
    onNext();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold text-[--oro-text-primary]">
          Stay in the loop
        </h2>
        <p className="text-[--oro-text-secondary]">
          Get notified about upcoming events, RSVPs, and community updates.
        </p>
      </div>

      {/* Illustration */}
      <div className="h-32 rounded-xl bg-[--oro-surface-secondary] flex items-center justify-center">
        <span className="text-[--oro-text-tertiary] text-sm">
          Notification icon
        </span>
      </div>

      {/* Status Message */}
      {status === 'granted' && (
        <div className="px-4 py-3 rounded-lg bg-green-500/10 border border-green-500/20">
          <p className="text-sm text-green-600 dark:text-green-400 font-medium">
            ✓ Notifications enabled
          </p>
        </div>
      )}

      {status === 'denied' && (
        <div className="px-4 py-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
          <p className="text-sm text-yellow-600 dark:text-yellow-400">
            You can enable notifications later in settings.
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={onBack}
          disabled={status === 'requesting'}
          className="px-6 py-2.5 rounded-lg bg-[--oro-surface-secondary] text-[--oro-text-primary] font-medium hover:bg-[--oro-surface-tertiary] transition-colors disabled:opacity-50"
        >
          Back
        </button>
        
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleSkip}
            disabled={status === 'requesting'}
            className="px-6 py-2.5 rounded-lg text-[--oro-text-secondary] font-medium hover:text-[--oro-text-primary] transition-colors disabled:opacity-50"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={handleEnableNotifications}
            disabled={status === 'requesting' || status === 'granted'}
            className="px-6 py-2.5 rounded-lg bg-[--oro-accent-primary] text-[--oro-text-on-accent] font-medium hover:bg-[--oro-accent-hover] transition-colors disabled:opacity-50"
          >
            {status === 'requesting' ? 'Enabling...' : 'Enable Notifications'}
          </button>
        </div>
      </div>
    </div>
  );
}
